import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  FormHelperText,
  Typography
} from '@mui/material';

const WorkAreaDialog = ({ 
  open, 
  onClose, 
  onSubmit, 
  workArea, 
  floors, 
  isEditing 
}) => {
  const [currentWorkArea, setCurrentWorkArea] = useState(workArea);
  
  // Reset currentWorkArea when the workArea prop changes
  useEffect(() => {
    setCurrentWorkArea(workArea);
  }, [workArea]);

  // Input change handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentWorkArea({ ...currentWorkArea, [name]: value });
  };

  // Submit handler
  const handleSubmit = () => {
    // Prepare the work area data for submission
    const updatedWorkArea = {
      ...currentWorkArea,
      area_type: currentWorkArea?.area_type || 'OP', // Default to Open Space
      status: currentWorkArea?.status || 'AC',       // Default to Active
    };
    onSubmit(updatedWorkArea);
  };

  // Area type options
  const areaTypes = [
    { value: 'OP', label: 'Open Space' },
    { value: 'SU', label: 'Suite' }
  ];

  // Status options
  const statusOptions = [
    { value: 'AC', label: 'Active' },
    { value: 'IN', label: 'Inactive' }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {isEditing ? 'Edit Work Area' : 'Add New Work Area'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Work Area Name */}
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Work Area Name"
              value={currentWorkArea?.name || ''}
              onChange={handleInputChange}
              fullWidth
              required
              placeholder="e.g., Main Floor, Quiet Zone, Open Workspace"
              helperText="Enter a descriptive name for this work area"
            />
          </Grid>
          
          {/* Floor Selection */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Floor</InputLabel>
              <Select
                name="floor_slug"
                value={currentWorkArea?.floor_slug || ''}
                onChange={handleInputChange}
                label="Floor"
              >
                {floors && floors.map(floor => (
                  <MenuItem key={floor.slug} value={floor.slug}>
                    {floor.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select which floor this work area is located on
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Area Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Area Type</InputLabel>
              <Select
                name="area_type"
                value={currentWorkArea?.area_type || 'OP'}
                onChange={handleInputChange}
                label="Area Type"
              >
                {areaTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Open Space: Shared workspace | Suite: Private office space
              </FormHelperText>
            </FormControl>
          </Grid>
          
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              name="description"
              label="Description"
              value={currentWorkArea?.description || ''}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Describe this work area, its features, and amenities..."
              helperText="Optional: Provide details about the work area to help users understand what's available"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={currentWorkArea?.status || 'AC'}
                onChange={handleInputChange}
                label="Status"
              >
                {statusOptions.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Active areas are available for booking, inactive areas are hidden
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Information Box for New Work Areas */}
          {!isEditing && (
            <Grid item xs={12}>
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '4px',
                border: '1px solid #bbdefb'
              }}>
                <Typography variant="body2" color="primary" gutterBottom>
                  <strong>What happens after creation:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • You can configure hot desks, services, and pricing for this work area
                  • Upload photos to showcase the space
                  • Set specific amenities and features available
                  • The work area will be available for booking once configured
                </Typography>
              </div>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={
            !currentWorkArea?.name || 
            !currentWorkArea?.floor_slug
          }
        >
          {isEditing ? 'Update Work Area' : 'Create Work Area'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkAreaDialog;